import {
  contrastOutline,
  idCard,
  mail,
  moonOutline,
  person,
  personCircleOutline,
  shieldCheckmark,
  sunnyOutline,
} from 'ionicons/icons';
import { useUserProfile } from '@/hooks/useUserProfile';
import { Card, CardContent, CardHeader, CardSubtitle } from '@/components/Card';
import { Icon } from '@/components/Icon';
import { PageSpinner } from '@/components/PageSpinner';
import { ErrorState } from '@/components/ErrorState';
import { useThemePreference, type ThemePreference } from '@/state/ThemePreference';

/**
 * Port of user-profile.page.ts / user-profile.page.html.
 *
 * The Angular template also had an "Account status" card with two
 * checkmark-circle icons, commented out pending a customer-status feature. It
 * isn't carried over; add it here when that feature exists.
 */
export default function UserProfilePage() {
  // Read through the query object so isPending/isError narrow `data`.
  const profileQuery = useUserProfile();

  if (profileQuery.isPending) return <PageSpinner message="Loading your profile..." />;
  if (profileQuery.isError) {
    return (
      <ErrorState
        title="Could not load your profile"
        error={profileQuery.error}
        onRetry={() => void profileQuery.refetch()}
      />
    );
  }

  const data = profileQuery.data;

  const fields = [
    { label: 'User ID / email address', value: data.email, icon: mail },
    { label: 'Full name', value: data.name, icon: person },
    { label: 'Customer ID', value: data.customerId, icon: idCard },
  ];

  return (
    <div className="mx-auto max-w-2xl px-4 py-6">
      {/* The Angular .profile-header: an 80px orange avatar over the title. */}
      <header className="mb-8 text-center">
        <div className="mx-auto mb-4 flex size-20 items-center justify-center rounded-full bg-primary">
          <Icon icon={personCircleOutline} className="size-20 text-white" />
        </div>
        <h1 className="text-2xl font-semibold text-primary">User profile</h1>
      </header>

      <div className="space-y-4">
        {fields.map((field) => (
          <Card key={field.label}>
            <CardHeader>
              <CardSubtitle icon={field.icon}>{field.label}</CardSubtitle>
            </CardHeader>
            <CardContent>
              <p className="text-content">{field.value || '—'}</p>
            </CardContent>
          </Card>
        ))}

        <Card>
          <CardHeader>
            <CardSubtitle icon={shieldCheckmark}>Role</CardSubtitle>
          </CardHeader>
          <CardContent>
            <span className="inline-flex rounded-full bg-band px-3 py-1 text-sm font-medium text-content capitalize">
              {data.role}
            </span>
          </CardContent>
        </Card>

        <AppearanceSetting />
      </div>
    </div>
  );
}

const THEME_OPTIONS: { value: ThemePreference; label: string; icon: string; hint: string }[] = [
  { value: 'system', label: 'System', icon: contrastOutline, hint: 'Match your device' },
  { value: 'light', label: 'Light', icon: sunnyOutline, hint: 'Always light' },
  { value: 'dark', label: 'Dark', icon: moonOutline, hint: 'Always dark' },
];

/**
 * Radio buttons rather than a row of plain buttons: this is one choice out of
 * three, so arrow keys move between the options and a screen reader announces
 * it as a group. The inputs are visually hidden; the labels carry the styling.
 */
function AppearanceSetting() {
  const { preference, resolved, setPreference } = useThemePreference();

  return (
    <Card>
      <CardHeader>
        <CardSubtitle icon={resolved === 'dark' ? moonOutline : sunnyOutline}>
          Appearance
        </CardSubtitle>
      </CardHeader>
      <CardContent>
        <fieldset>
          <legend className="sr-only">Appearance</legend>
          <div className="grid gap-2 sm:grid-cols-3">
            {THEME_OPTIONS.map((option) => {
              const isSelected = preference === option.value;
              return (
                <label
                  key={option.value}
                  className={[
                    'flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors',
                    'has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-focus',
                    isSelected
                      ? 'border-primary bg-primary/10 text-content'
                      : 'border-line text-content-secondary hover:bg-hover',
                  ].join(' ')}
                >
                  <input
                    type="radio"
                    name="appearance"
                    value={option.value}
                    checked={isSelected}
                    onChange={() => setPreference(option.value)}
                    className="sr-only"
                  />
                  <Icon
                    icon={option.icon}
                    className={`size-6 ${isSelected ? 'text-primary' : 'text-muted'}`}
                  />
                  <span className="min-w-0">
                    <span className="block font-medium">{option.label}</span>
                    <span className="block text-sm text-muted">{option.hint}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </fieldset>

        {preference === 'system' && (
          <p className="mt-3 text-sm text-muted">
            Your device is currently set to {resolved}. Set your device to switch automatically
            and this will follow it at sunset.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
